import { useState, useMemo } from "react";

export const SLIDE_SPLIT_SYMBOLS = [",", ".", ";", ":", "!", "?", '"'];

export interface SlideOptionsData {
  upperCase: boolean;
  referenceAbove: boolean;
  charactersPerLine: number;
  linesPerSlide: number;
  newSlideAfter: string[];
}

export const DEFAULT_SLIDE_OPTIONS_DATA: SlideOptionsData = {
  upperCase: false,
  referenceAbove: true,
  charactersPerLine: 55,
  linesPerSlide: 4,
  newSlideAfter: [...SLIDE_SPLIT_SYMBOLS],
};

interface Props {
  onUpdate: (e: SlideOptionsData) => void;
}

const SlideOptions = (props: Props) => {
  const [slideOptionsData, setSlideOptionsData] = useState(DEFAULT_SLIDE_OPTIONS_DATA);

  // Generic state modifier
  const stateModifier = <K extends keyof SlideOptionsData>(key: K, value: SlideOptionsData[K]) => {
    const updated = { ...slideOptionsData, [key]: value };
    setSlideOptionsData(updated);
    props.onUpdate(updated);
  };

  // Toggle symbols
  const toggleSlideSplitSymbol = (symbol: string) => {
    const newSymbols = slideOptionsData.newSlideAfter.includes(symbol)
      ? slideOptionsData.newSlideAfter.filter((s) => s !== symbol)
      : [...slideOptionsData.newSlideAfter, symbol];
    stateModifier("newSlideAfter", newSymbols);
  };

  // Memoized symbol checkboxes
  const symbols = useMemo(
    () =>
      SLIDE_SPLIT_SYMBOLS.map((sym, i) => (
        <div key={sym} className="inline-flex mx-3 text-slate-700 items-center">
          <input
            type="checkbox"
            id={`symbol-${i}`}
            onChange={() => toggleSlideSplitSymbol(sym)}
            checked={slideOptionsData.newSlideAfter.includes(sym)}
          />
          <label className="mx-1 mb-1" htmlFor={`symbol-${i}`}>
            {sym}
          </label>
        </div>
      )),
    [slideOptionsData.newSlideAfter]
  );

  return (
    <div className="m-5 p-5 border rounded">
      <h2 className="text-xl mb-3 text-slate-900">Options:</h2>
      <div className="ml-1">
        {/* Upper-case checkbox */}
        <div className="inline-flex mr-8 my-1 text-slate-700 items-center">
          <input
            type="checkbox"
            id="upper-case"
            onChange={(e) => stateModifier("upperCase", e.currentTarget.checked)}
            checked={slideOptionsData.upperCase}
          />
          <label className="ml-2" htmlFor="upper-case">
            Upper-case
          </label>
        </div>

        {/* Reference above checkbox */}
        <div className="inline-flex mr-8 my-1 text-slate-700 items-center">
          <input
            type="checkbox"
            id="reference-position"
            onChange={(e) => stateModifier("referenceAbove", e.currentTarget.checked)}
            checked={slideOptionsData.referenceAbove}
          />
          <label className="ml-2" htmlFor="reference-position">
            Reference above
          </label>
        </div>

        {/* Characters per line */}
        <div className="inline-flex mr-8 my-1 text-slate-700 items-center">
          <label className="mr-2 text-slate-700" htmlFor="characters-per-line">
            Characters per line:
          </label>
          <input
            type="number"
            id="characters-per-line"
            min={1}
            className="w-[50px] ml-2 pl-1 text-slate-700 border rounded focus:outline-none"
            onChange={(e) => stateModifier("charactersPerLine", Math.max(1, Number(e.currentTarget.value)))}
            value={slideOptionsData.charactersPerLine}
          />
        </div>

        {/* Lines per slide */}
        <div className="inline-flex mr-8 my-1 text-slate-700 items-center">
          <label className="mr-2" htmlFor="lines-per-slide">
            Lines per slide:
          </label>
          <input
            type="number"
            id="lines-per-slide"
            min={1}
            className="w-[50px] ml-2 pl-1 text-slate-700 border rounded focus:outline-none"
            onChange={(e) => stateModifier("linesPerSlide", Math.max(1, Number(e.currentTarget.value)))}
            value={slideOptionsData.linesPerSlide}
          />
        </div>
      </div>

      {/* Symbols */}
      <div className="ml-1 mt-2 text-slate-700">
        <label className="mr-3">New slide after:</label>
        {symbols}
      </div>
    </div>
  );
};

export default SlideOptions;
